import { useState } from "react";

import BlockIcon from "@mui/icons-material/BlockRounded";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import type { ParticipantFormType } from "@shared";
import { ParticipantFormFields } from "@shared";
import type { FormikProps } from "formik";

import { FormDialog } from "@/common/components/form-dialog/FormDialog";
import { DeactivateParticipantDialog } from "@/pages/system-settings/tabs/proceeding-participants/components/edit-participant/deactivate-participant-dialog/DeactivateParticipantDialog";
import { useTranslation } from "@/translations";

interface Props {
  participantId: string;
  participantName: string;
  formik: FormikProps<ParticipantFormType>;
  isLoading: boolean;
  hasParticipant: boolean;
  lastChangedText: string | null;
  mergeHistory: string | null;
  onClose: () => void;
}

export function ActiveParticipantEditDialog({
  participantId,
  participantName,
  formik,
  isLoading,
  hasParticipant,
  lastChangedText,
  mergeHistory,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);

  return (
    <>
      <FormDialog
        title={t("proceedingParticipants.edit.dialogTitle")}
        onClose={onClose}
        formik={formik}
        submitLabel={t("common.save")}
        submitDisabled={!formik.dirty}
        paperSx={{ width: 960 }}
        extraAction={
          hasParticipant
            ? {
                left: (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<BlockIcon />}
                    onClick={() => setIsDeactivateOpen(true)}
                  >
                    {t("proceedingParticipants.edit.deactivate")}
                  </Button>
                ),
                right:
                  lastChangedText != null ? (
                    <Typography variant="body2" color="text.secondary">
                      {lastChangedText}
                    </Typography>
                  ) : undefined,
              }
            : undefined
        }
      >
        {isLoading || !hasParticipant ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {mergeHistory && (
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t("proceedingParticipants.merge.historyLabel")}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {mergeHistory}
                </Typography>
              </Stack>
            )}
            <ParticipantFormFields formik={formik} typeDisabled />
          </Stack>
        )}
      </FormDialog>

      {isDeactivateOpen && hasParticipant && (
        <DeactivateParticipantDialog
          participantId={participantId}
          participantName={participantName}
          onClose={() => setIsDeactivateOpen(false)}
          onDeactivated={() => {
            setIsDeactivateOpen(false);
            onClose();
          }}
        />
      )}
    </>
  );
}
